import { useSchoolYear } from '@pelckmans/business-components/context/SchoolYearContext';
import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Page from '@pelckmans/elementis/components/page';
import Tabs from '@pelckmans/elementis/components/tabs';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { fetchGroups, Group } from '../../api/groups';
import ModalContext from '../../contexts/ModalContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useRoutes } from '../../hooks/useRoutes';
import useThrowAsyncError from '../../hooks/useThrowAsyncError';
import ContentBody from '../../layout/content/ContentBody';
import ContentHeader from '../../layout/content/ContentHeader';
import ModuleBreadcrumbs from '../../layout/header/ModuleBreadcrumbs';
import AddLicenseModal from '../../shared-components/AddLicenseModal';
import {
  withScrollThreshold,
  WithScrollThresholdProps,
} from '../../shared-components/elementis/hoc/withScrollThreshold';
import ModuleContext from '../material/_contexts/ModuleContext';
import AllAssignments from './components/Assignments';

const BASE = 'PAGES.ASSIGNMENTS';

const TABS = {
  PLANNING: 'PLANNING',
  ARCHIVE: 'ARCHIVE',
};

interface AssignmentsProps extends WithScrollThresholdProps {}

function Assignments({ pastThreshold }: AssignmentsProps) {
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groups, setGroups] = useState<Group[]>([]);

  const { module, refetch } = useContext(ModuleContext);

  const { t } = useTranslation();
  const { currentSchoolYear } = useSchoolYear();
  const handleError = useThrowAsyncError();
  const { showModal } = useContext(ModalContext);

  const hasDigitalManualLicense = module?.licenses.hasDigitalManualLicenseForTeacher;

  const { buildAbsolutePath } = useRoutes();
  const params = {
    subjectSlug: module?.subjectSlug || '',
    methodSlug: module?.methodSlug || '',
    moduleSlug: module?.slug || '',
  };

  useDocumentTitle(t(`${BASE}.DOCUMENT_TITLE`));

  useEffect(() => {
    const getGroupsOfSchoolYear = async () => {
      setIsLoadingGroups(true);

      const fetchedGroups = await fetchGroups({
        startYear: currentSchoolYear.from.getFullYear(),
        endYear: currentSchoolYear.to.getFullYear(),
      });

      setGroups(fetchedGroups);
      setIsLoadingGroups(false);
    };

    getGroupsOfSchoolYear().catch(handleError);
  }, [handleError, currentSchoolYear]);

  return (
    <Page>
      <Tabs>
        <ContentHeader
          cover={
            <Page.Header
              density={pastThreshold ? 'compact' : 'normal'}
              breadcrumbs={<ModuleBreadcrumbs size={pastThreshold ? 'sm' : 'md'} />}
              headerText={t(`${BASE}.PAGE_TITLE`)}
              extra={
                <Tabs.TabList aria-label={t(`${BASE}.TAB_LIST.A11Y_LABEL`)} size={pastThreshold ? 'sm' : 'md'}>
                  <Tabs.Tab id={TABS.PLANNING}>{t(`${BASE}.TAB_LIST.TABS.PLANNING`)}</Tabs.Tab>
                  <Tabs.Tab id={TABS.ARCHIVE}>{t(`${BASE}.TAB_LIST.TABS.ARCHIVED`)}</Tabs.Tab>
                </Tabs.TabList>
              }
            />
          }
        />
        <ContentBody
          alerts={
            hasDigitalManualLicense ? (
              <Alert prominence="info">
                {t(`${BASE}.ALERTS.ADD_ASSIGNMENT.TEXT_1`)}
                <Link to={buildAbsolutePath(['MATERIAL'], params)}>{t(`${BASE}.ALERTS.ADD_ASSIGNMENT.LINK`)}</Link>
                {t(`${BASE}.ALERTS.ADD_ASSIGNMENT.TEXT_2`)}
              </Alert>
            ) : (
              <Alert
                prominence="warning"
                actions={{
                  'aria-label': t(`${BASE}.ALERTS.NO_LICENSE.BUTTON.A11Y_LABEL`),
                  size: 'sm',
                  role: undefined,
                  children: (
                    <Button
                      prominence="warning"
                      onClick={() => showModal(<AddLicenseModal id="add-license-modal" onRefetch={refetch} />)}
                    >
                      {t(`${BASE}.ALERTS.NO_LICENSE.BUTTON.TEXT`)}
                    </Button>
                  ),
                }}
              >
                {t(`${BASE}.ALERTS.NO_LICENSE.TEXT`)}
              </Alert>
            )
          }
        >
          <div className="assignments-section">
            <Tabs.TabsContent>
              <Tabs.TabPanel id={TABS.PLANNING}>
                <AllAssignments groups={groups} isLoadingGroups={isLoadingGroups} />
              </Tabs.TabPanel>
              <Tabs.TabPanel id={TABS.ARCHIVE}>
                <AllAssignments groups={groups} isLoadingGroups={isLoadingGroups} archived />
              </Tabs.TabPanel>
            </Tabs.TabsContent>
          </div>
        </ContentBody>
      </Tabs>
    </Page>
  );
}

export default withScrollThreshold(Assignments);
